import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Inject, Input, OnDestroy, OnInit, Output, PLATFORM_ID } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, ParamMap, Router, RouterLink } from '@angular/router';
import { isPlatformBrowser, Location, ViewportScroller } from '@angular/common';

import { combineLatest, Observable, of, Subject } from 'rxjs';
import { catchError, map, switchMap, takeUntil, tap } from 'rxjs/operators';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';

import { CmsService, Page, Product } from '@spartacus/core';

import { SdPaginationComponent } from '@seb/web-design-system/pagination';
import { BrandsEnum, IconSizeEnum, ILinkCard, IProductCustomer, ISliderInfo, PositionEnum, SdBannerComponent, SdBorderWrapperComponent, SdButtonComponent,
  SdFormAutocompleteComponent, SdIconComponent, SdLinkCardComponent, SdListComponent, SdLoaderComponent, SdModalDefaultComponent,
  SdProductCustomerComponent, SizesEnum } from '@seb/web-design-system/core';
import { AutocompleteSuggestion, CssHierarchy, CustomCategory } from '../../../models';
import { LinkMapper, ProductMapper } from '../../../mappers';
import { BaseStoreService, GlobalSearchService, HobStateService, ImageService,
  PaginationComponentService, TrackingService, UrlMappingService } from '../../../services';

@Component({
  selector: 'wc-sis-categories',
  templateUrl: './sis-categories.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    SdBorderWrapperComponent,
    FormsModule,
    ReactiveFormsModule,
    SdFormAutocompleteComponent,
    SdIconComponent,
    SdButtonComponent,
    SdListComponent,
    SdLinkCardComponent,
    SdProductCustomerComponent,
    SdPaginationComponent,
    RouterLink,
    SdLoaderComponent,
    SdModalDefaultComponent,
    SdBannerComponent,
    TranslatePipe
  ]
})
export class SisCategoriesComponent implements OnInit, OnDestroy {
  linkCards: ILinkCard[] = [];
  productInfos: IProductCustomer[] = [];
  success = false;

  previousCategoryButton = false;

  urlParentCategoryCode: string;

  label: string;

  description: string;

  autocompleteElement = [];

  isRefModalOpened = false;

  formGroup: FormGroup;

  urlImage = '/assets/images/asset-accessory-reference.png';

  isRegister: boolean;

  title: string;

  subTitle: string;

  disableSubmit = true;

  isLoading = true;

  baseSite = '';

  storedCurrentPage: Page;
  sizesEnum = SizesEnum;
  positionEnum = PositionEnum;
  iconSize = IconSizeEnum;

  currentPage = 0;
  paginationTotalPages = 0;
  pageSize = 10;
  currentProductInfos: IProductCustomer[] = [];
  linkUrl = '';
  sisCategories: CssHierarchy;
  activeBrand: BrandsEnum;
  isHob: boolean;

  @Input()
  showAllRef = false;

  @Input()
  hasBorder = true;

  @Output()
  selectedProduct = new EventEmitter<Product>();

  private products: Product[] = [];

  private readonly destroy$: Subject<void> = new Subject();

  constructor(
    private cmsService: CmsService,
    private imageService: ImageService,
    private baseStoreService: BaseStoreService,
    private productMapper: ProductMapper,
    private activatedRoute: ActivatedRoute,
    private formBuilder: FormBuilder,
    private router: Router,
    private globalSearchService: GlobalSearchService,
    private trackingService: TrackingService,
    private translateService: TranslateService,
    private urlMappingService: UrlMappingService,
    private cdRef: ChangeDetectorRef,
    private viewportScroller: ViewportScroller,
    private location: Location,
    private paginationComponentService: PaginationComponentService,
    private readonly hobStateService: HobStateService,
    @Inject(PLATFORM_ID) private platformId: any
  ) {}

  ngOnInit(): void {
    this.formGroup = this.formBuilder.group({
      autocomplete: []
    });

    if (isPlatformBrowser(this.platformId)) {
      this.viewportScroller.setOffset([0, 200]);
    }

    const routeParam$ = this.activatedRoute.params;
    const queryParam$ = this.activatedRoute.queryParams;
    const currentPage$ = this.cmsService.getCurrentPage();
    const hobState$ = this.hobStateService.hobState$;
    const baseStore = this.baseStoreService.baseStore;

    combineLatest([routeParam$, currentPage$, queryParam$, hobState$])
      .pipe(
        tap(
          ([routeParam]) =>
            (this.previousCategoryButton = !!routeParam['categoryCode'] && routeParam['categoryCode'] !== 'products')
        ),
        switchMap(([routeParam, currentPage, routeQueryParam, { isHob, liveBrand, siteBrand }]) => {
          this.isHob = isHob;
          this.urlImage = isHob ? '/assets/images/hob-asset-accessory-reference.png' : this.urlImage;
          this.activeBrand = liveBrand || siteBrand;
          this.storedCurrentPage = currentPage;
          this.baseSite = baseStore.brandSite;
          if (currentPage?.label === '/faq/csc/products') {
            this.label = 'FAQ';
          } else if (currentPage?.label === '/instructions-for-use') {
            this.label = 'IFU';
          } else if (currentPage?.label === '/my-products') {
            this.label = 'RP';
          }
          if (!!routeQueryParam['query'] && !!routeQueryParam['target']) {
            this.previousCategoryButton = true;
            return this.globalSearchService.globalSearch(routeQueryParam)?.pipe(
              map(
                (globalSearchResult): CssHierarchy => ({
                  registerProductsCategory: {
                    products: globalSearchResult.productSearchPageData?.products || [],
                    parentCategoryCode: 'products',
                    name: routeQueryParam['query']
                  }
                })
              )
            );
          }
          return this.baseStoreService.getSisCategories(this.label, routeParam['categoryCode']);
        }),
        takeUntil(this.destroy$)
      )
      .subscribe((sisCategories) => {
        this.sisCategories = sisCategories;
        this.isRegister = false;
        this.linkCards = [];
        this.productInfos = [];
        if (sisCategories.faqCategory) {
          this.urlParentCategoryCode = '/faq/csc/'.concat(sisCategories.faqCategory.parentCategoryCode || '');
          this.mapSisCategories(sisCategories.faqCategory);
          this.description = sisCategories.faqCategory.description;
          this.title = this.storedCurrentPage['h1Tag']
            ? this.storedCurrentPage['h1Tag']?.replace(/<name>/gi, sisCategories.faqCategory.name)
            : `${this.translateService.instant('SEO.faqTitle')} ${sisCategories.faqCategory.name} ${this.baseSite} `;
        } else if (sisCategories.ifuCategory) {
          this.urlParentCategoryCode =
            sisCategories.ifuCategory.parentCategoryCode !== 'products'
              ? '/instructions-for-use/csc/'.concat(sisCategories.ifuCategory.parentCategoryCode)
              : '/instructions-for-use';
          this.mapSisCategories(sisCategories.ifuCategory);
          this.description = sisCategories.ifuCategory.description;
          this.updateTitle();
        } else if (sisCategories.registerProductsCategory) {
          this.isRegister = true;
          this.urlParentCategoryCode =
            sisCategories.registerProductsCategory.parentCategoryCode !== 'products'
              ? '/my-products/'.concat(sisCategories.registerProductsCategory.parentCategoryCode || '')
              : '/my-products';
          this.mapSisCategories(sisCategories.registerProductsCategory, true);
          this.title = this.translateService.instant('myProduct.registerProduct.title');
          this.subTitle = this.translateService.instant('myProduct.registerProduct.description1');
          this.description = this.translateService.instant('myProduct.registerProduct.description2');
        }

        this.paginationComponentService
          .getUrlWithParams()
          .pipe(takeUntil(this.destroy$))
          .subscribe((result) => {
            this.linkUrl = result;
          });
        this.isLoading = false;
        this.cdRef.markForCheck();
      });

    this.formGroup
      .get('autocomplete')
      .valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((value) => {
        this.disableSubmit = value?.length < 3;
        this.cdRef.markForCheck();
      });
  }

  searchProduct(input: string): void {
    if (input.length > 1) {
      this.globalSearchService
        .simpleAutocompleteSearch(input)
        .pipe(takeUntil(this.destroy$))
        .subscribe((result) => {
          this.autocompleteElement = result.results as AutocompleteSuggestion[];
          this.cdRef.markForCheck();
        });
    } else {
      this.autocompleteElement = [];
    }
  }

  openModalRef(): void {
    this.isRefModalOpened = true;
  }

  onSubmit(): void {

    let query = this.formGroup.get('autocomplete').value;
    if (this.isHob) {
      query += `:default:brand:${this.activeBrand}`;
    }

    this.navigateToSearch(query, true);
  }

  private navigateToSearch(query: string, isAccessorySearch?: boolean): void {
    if (this.label === 'RP') {
      this.router.navigate([], {
        relativeTo: this.activatedRoute,
        queryParams: {
          query,
          target: 'product',
          ...(isAccessorySearch !== undefined && { isAccessorySearch })
        },
        queryParamsHandling: 'merge'
      });
    } else {
      this.router.navigateByUrl('/search?query=' + query + '&target=' + this.label?.toLowerCase());
    }
    this.trackingService.sendEvent('search', {
      request: query
    });
  }

  selectProduct(product: IProductCustomer): void {
    const selectedProduct = this.products?.find((p) => p.code === product.cmmf);
    this.selectedProduct.emit(selectedProduct);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private mapSisCategories(sisCategories: CustomCategory, isRegisterProducts?: boolean): void {
    if (sisCategories.subCategories) {
      const linkCards$ = sisCategories.subCategories.map((category) => this.categoryToILinkCard(category));
      combineLatest(linkCards$)
        .pipe(takeUntil(this.destroy$))
        .subscribe((linkCards) => {
          this.linkCards = linkCards;
        });
    } else if (sisCategories.products) {
      const products = sisCategories.products;
      const productCustomer$ = products.map((product) => this.productMapper.productToProductCustomer(product));
      combineLatest([this.activatedRoute.queryParamMap, ...productCustomer$])
        .pipe(takeUntil(this.destroy$))
        .subscribe(([queryParamsMap, ...productCustomer]: [ParamMap, IProductCustomer]) => {
          if (isRegisterProducts) {
            this.products = sisCategories.products;
            this.productInfos = sisCategories.products.map((product, index) => {
              const productInfo = productCustomer[index];
              delete productInfo.url;
              return productInfo;
            });
          } else {
            this.productInfos = sisCategories.products.map((product, index) => productCustomer[index]);
          }
          this.paginationTotalPages = Math.ceil(this.productInfos.length / this.pageSize);
          this.currentProductInfos = this.productInfos.slice(0, this.pageSize);
          this.currentPage = queryParamsMap.has('page') ? parseInt(queryParamsMap.get('page')) - 1 : 0;
          if (this.currentPage > 0) {
            this.getProductInfos({ index: this.currentPage, itemsPerView: this.pageSize });
          }
        });
    }
  }

  private categoryToILinkCard(category: CustomCategory): Observable<ILinkCard> {
    const categoryUrl = LinkMapper.decodeUrl(category.url);
    return this.urlMappingService.getUrlMappingByCode(categoryUrl).pipe(
      catchError(() => of(null)),
      map((urlMapping) => {
        let finalCategoryUrl = categoryUrl;
        if (urlMapping) {
          finalCategoryUrl = categoryUrl.replace(urlMapping.code, urlMapping.value);
        }
        return {
          media: this.imageService.normalizeMedia(category.image),
          link: LinkMapper.toLink(finalCategoryUrl, category.name)
        };
      })
    );
  }

  getPage(event: ISliderInfo) {
    this.getProductInfos(event);

    const simpleLinkUrl = this.linkUrl.includes('?page=')
      ? this.linkUrl.replace('?page=', '')
      : this.linkUrl.replace('&page=', '');
    const redirectUrl = this.currentPage ? this.linkUrl + (this.currentPage + 1) : simpleLinkUrl;
    this.location.replaceState(redirectUrl);
    this.updateTitle();
  }

  getProductInfos(event: ISliderInfo) {
    this.currentPage = event.index;
    const start = this.pageSize * this.currentPage;
    const end = start + this.pageSize;
    this.currentProductInfos = this.productInfos.slice(start, end);
  }

  updateTitle(): void {
    this.title = this.storedCurrentPage['h1Tag']?.replace(/<name>/gi, this.sisCategories?.ifuCategory?.name);
  }

  onClickedPage(index: number) {
    if (isPlatformBrowser(this.platformId)) {
      this.viewportScroller.scrollToAnchor('product-customer-list');
    }
    this.trackingService.sendEvent('clickPagination', { paginationLevel: index });
    this.getPage({ index: index - 1, itemsPerView: this.pageSize });
  }
}
